import Cookies from 'js-cookie';
import _, { get as getData } from 'lodash';

// File in the same folder
import { get, post, request } from './../session/actions';
import { getServicesErrorObj } from '../servicesDowntime/actions';
// Types
import {
  REGISTER_CUSTOMER_REQUEST_START,
  REGISTER_CUSTOMER_REQUEST_FAIL,
  REGISTER_CUSTOMER_REQUEST_SUCCESS,
  UPDATE_REGISTER_CUSTOMER_STATUS_TO_INITIAL,
  SEND_SKIP_DOCUMENT_EMAIL_START,
  SEND_SKIP_DOCUMENT_EMAIL_FAIL,
  SEND_SKIP_DOCUMENT_EMAIL_SUCCESS
} from './types';
import { AffiliateDetailsAttributes } from '../../../core/types/AffiliateDetails';
// Utils
import { FormatDate } from '../../../core/utils/Formatter/DateFormat';
import { getReferringCompany } from '../../../core/utils/ReferringCompanyValueGetter/ReferringCompanyValueGetter';
// Constants
import {
  CDH_COMPANY_ORG_CODE_MAPPING,
  CDSA_REFERRING_VALUE,
  ORGANIZATION__CODE_MAPPING,
  SSO_CDH_COMPANY_KEY
} from '../../../core/utils/Constants/Constants';

const SKIP_DOCUMENT_SEND_EMAIL_SUBJECT = 'Skip document notification';
const CUSTOMER_DOCUMENT_TYPE_BOTH = 'both';

export const registerCustomerRequestStart = () => ({
  type: REGISTER_CUSTOMER_REQUEST_START
});

export const registerCustomerRequestFail = (payload?: any) => ({
  type: REGISTER_CUSTOMER_REQUEST_FAIL,
  payload
});

export const registerCustomerRequestSuccess = (payload: any) => ({
  type: REGISTER_CUSTOMER_REQUEST_SUCCESS,
  payload
});

export const updateRegisterCustomerStatusToInitial = () => ({
  type: UPDATE_REGISTER_CUSTOMER_STATUS_TO_INITIAL
});

export const sendSkipDocumentEmailRequestStart = () => ({
  type: SEND_SKIP_DOCUMENT_EMAIL_START
});


export const sendSkipDocumentEmailRequestFail = () => ({
  type: SEND_SKIP_DOCUMENT_EMAIL_FAIL
});

export const sendSkipDocumentEmailRequestSuccess = (payload) => ({
  type: SEND_SKIP_DOCUMENT_EMAIL_SUCCESS,
  payload
});

const REGISTER_CUSTOMER_URL: Function = (AFFILIATE_ID: string) => `/affiliate-portal/ClientRegistration/registerCustomer?affiliateNumber=${AFFILIATE_ID}`;
const GET_CUSTOMER_IDENTIFIER_URL: Function = (customer_email: string) => `/affiliate-portal/ClientRegistration/checkTANStatus?email=${customer_email}`;
const GET_CUSTOMER_URL: Function = (customer: string) => `/v1/customers/${customer}/details`;
const SKIP_DOCUMENT_SEND_EMAIL_URL: string = '/v1/affiliate-portal/registration/skipdocumentemail';

const MAX_ATTEMPTS: number = 12;
const POLL_INTERVAL: number = 10 * 1000;
const POLLING_COMPLETE_MSG: string = 'POLLING_COMPLETE';

const getHeaderPacket = () => {
  const referringCompany = getReferringCompany();
  const organization: string = ORGANIZATION__CODE_MAPPING[referringCompany];

  return {
    'X-Organization': referringCompany === CDSA_REFERRING_VALUE ? "CD SA" : organization,
    'X-Timestamp': new Date().toISOString(),
    'X-Username': 'affiliate portal'
  };
};

export const getCustomerId = (customer_email: string, unique_identifier: string, organization: string, attempt: number = 1) => (
  dispatch: Function
) => new Promise((resolve, reject) => {
  dispatch(
    request({
      url: GET_CUSTOMER_IDENTIFIER_URL(customer_email, unique_identifier, organization),
      method: "GET",
      headers: getHeaderPacket()
    })
  ).then(resolve).catch(() => {
    if (attempt < MAX_ATTEMPTS) {
      setTimeout(() => {
        dispatch(
          getCustomerId(customer_email, unique_identifier, organization, attempt + 1)
        ).then(resolve).catch(reject);
      }, POLL_INTERVAL);
    } else {
      reject(POLLING_COMPLETE_MSG);
    }
  });
});

export const getCustomerState = (id: string, attempt: number = 1) => (
  dispatch: Function
) => new Promise((resolve, reject) => {
  dispatch(
    request({
      url: get(GET_CUSTOMER_URL(id)),
      method: "GET",
      headers: getHeaderPacket()
    })
  ).then((response: any) => {
    resolve(response.data);
  }).catch(reject);
});

export const registerCustomer = (customer: any, affiliateDetails: AffiliateDetailsAttributes | null) => (
  dispatch: Function
) => new Promise((resolve, reject) => {
  dispatch(registerCustomerRequestStart());

  const dob = customer.dateOfBirth;
  const jointDoB = customer.jointDateOfBirth;

  let dob_str: string = FormatDate(dob);
  let joint_dob_str: string = customer.isJoint ? FormatDate(jointDoB) : '';

  const getDefaultAddressProperty = (property: string, defaultValue: string) => {
    return customer.address[property] || defaultValue;
  }

  const packet = {
    customer_type: "Personal",
    contacts: [
      {
        first_name: customer.firstName,
        last_name: customer.lastName,
        date_of_birth: dob_str,
        email: customer.email,
        mobile_number: `${customer.mobile.select}-${customer.mobile.input}`,
        primary_contact: true,
        authorize_to_trade: true,
        employment_status: customer.employmentStatus,
        ...(customer.industryType && { industry: customer.industryType }),
        address: {
          street: getDefaultAddressProperty('line1', ' '),
          post_code: getDefaultAddressProperty('postcode', 'NOPOST'),
          city: getDefaultAddressProperty('city', ' '),
          state: getDefaultAddressProperty('county', ' '),
          country: getDefaultAddressProperty('country', ' '),
        }
      },
    ],
    additional_details: {
      ...(customer.clientReferenceId && { client_reference_id: customer.clientReferenceId }),
      etv: 'over 250,000',
      ccy_pair: `EURUSD`,
      channel: 'Affiliate Portal'
    }
  };

  customer.isJoint && packet.contacts.push({
    first_name: customer.jointFirstNames,
    last_name: customer.jointLastName,
    date_of_birth: joint_dob_str,
    email: customer.jointEmail,
    mobile_number: `${customer.jointMobile.select}-${customer.jointMobile.input}`,
    primary_contact: true,
    employment_status: customer.jointEmploymentStatus,
    industry: customer.jointIndustryType,
    authorize_to_trade: true,
    address: {
      street: customer.jointAddress.line1,
      post_code: customer.jointAddress.postcode,
      city: customer.jointAddress.city,
      state: customer.jointAddress.county,
      country: customer.jointAddress.country
    }
  });

  const fail = (currentStatus?: any) => {
    dispatch(registerCustomerRequestFail(currentStatus));
  }

  const AFFILIATE_ID: string = affiliateDetails?.affiliate.affiliate_Number ?? '';

  dispatch(
    request({
      url: REGISTER_CUSTOMER_URL(AFFILIATE_ID),
      method: "POST",
      data: {
        ...packet
      },
      headers: { ...getHeaderPacket(), source: 'Affiliate', sub_source: 'FIRPTA Registration' }
    })
  ).then((registration: any) => {
    const {
      data: {
        unique_identifier
      }
    } = registration;

    if (!unique_identifier) {
      fail();
      return;
    }

    const cdhCompany: any = Cookies.get(SSO_CDH_COMPANY_KEY) ?? Cookies.get('cdhCompany');

    const organization: string = CDH_COMPANY_ORG_CODE_MAPPING[cdhCompany];
    const customer_email: string = customer.email;

    dispatch(getCustomerId(customer_email, unique_identifier, organization))
      .then((customer_state: any) => {
        dispatch(registerCustomerRequestSuccess(customer_state.data));
        resolve(customer_state.data);
      })
      .catch((error: any) => {
        dispatch(registerCustomerRequestFail('CUSTOMER_STATE_FAIL'));
        dispatch(getServicesErrorObj(error));
      });

  }).catch(fail);
});

export const updateRegisterCustomerStatus = () =>
  (dispatch: Function) => {

    dispatch(updateRegisterCustomerStatusToInitial());
  };

export const sendSkipDocumentEmail = (customer_state, managerEmailId) =>
  (dispatch: Function) =>
    new Promise((resolve, reject) => {
      dispatch(sendSkipDocumentEmailRequestStart());

      const contacts = getData(customer_state, 'contacts'),
        primaryAccountDetails = _.filter(contacts, { primary_contact: true })[0],
        primaryAccountDocumentsRequired = getData(primaryAccountDetails, 'documents_required'),
        customerDocumentType = (primaryAccountDocumentsRequired?.length === 2) ?
          CUSTOMER_DOCUMENT_TYPE_BOTH :
          primaryAccountDocumentsRequired[0].type.toLowerCase();

      const singleAccountRequestBody = {
        "customer_first_name": getData(primaryAccountDetails, 'first_name'),
        "customer_last_name": getData(primaryAccountDetails, 'last_name'),
        "customer_email_address": getData(primaryAccountDetails, 'email'),
        "customer_number": getData(customer_state, 'customer_id'),
        "manager_email_address": managerEmailId,
        "subject": SKIP_DOCUMENT_SEND_EMAIL_SUBJECT,
        "customer_document_type": customerDocumentType
      };

      let skipDocumentEmailRequestBody = singleAccountRequestBody;

      if (contacts?.length > 1) {
        const secondaryAccountDetails = _.filter(contacts, { primary_contact: false })[0],
          secondaryAccountDocumentsRequired = getData(secondaryAccountDetails, 'documents_required'),
          jointCustomerDocumentType = (secondaryAccountDocumentsRequired?.length === 2) ?
            CUSTOMER_DOCUMENT_TYPE_BOTH :
            secondaryAccountDocumentsRequired[0].type.toLowerCase();

        let jointRequestBody = {
          ...singleAccountRequestBody,
          "joint_customer_first_name": getData(secondaryAccountDetails, 'first_name'),
          "joint_customer_last_name": getData(secondaryAccountDetails, 'last_name'),
          "joint_customer_email_address": getData(secondaryAccountDetails, 'email'),
          "joint_customer_document_type": jointCustomerDocumentType
        };
        skipDocumentEmailRequestBody = jointRequestBody;
      }

      dispatch(post(SKIP_DOCUMENT_SEND_EMAIL_URL, skipDocumentEmailRequestBody)).then((response: any) => {
        dispatch(sendSkipDocumentEmailRequestSuccess(response));
        resolve(response);

      }).catch((error: any) => {
        dispatch(sendSkipDocumentEmailRequestFail());
        dispatch(getServicesErrorObj(error));
        reject(error);
      })
    });


export type RegisterCustomerAction = {
  type: string,
  payload?: any
};
